import React from 'react'
import { AllHTMLAttributes } from 'react'

import theme from 'presentation/styles/theme'
import * as S from './styles'

export type TextAreaProps = Omit<
  AllHTMLAttributes<HTMLTextAreaElement>,
  'label'
> & {
  onInputChange?: (value: string) => void
  label?: React.ReactNode
  labelColor?: keyof typeof theme.ds.colors
  labelSize?: keyof typeof theme.ds.typography.size
  labelWeight?: keyof typeof theme.ds.typography.weight
  initialValue?: string
  icon?: React.ReactNode
  disabled?: boolean
  error?: string | false
  bgColor?: keyof typeof theme.ds.colors
  required?: boolean
  requiredColor?: keyof typeof theme.ds.colors
  outline?: boolean
  outlineColor?: keyof typeof theme.ds.colors
  outlineFocusColor?: keyof typeof theme.ds.colors
  dataTestId?: string | number
  textColor?: keyof typeof theme.ds.colors
  fontSize?: keyof typeof theme.ds.typography.size
  radius?: keyof typeof theme.ds.typography.border.radius
  labelOverlapping?: boolean
  labelBgColor?: keyof typeof theme.ds.colors
}

const TextAreaNew: React.ForwardRefRenderFunction<
  HTMLTextAreaElement,
  TextAreaProps
> = (
  {
    label,
    labelColor = 'neutral900',
    labelSize = 'small',
    labelWeight = 'normal',
    name,
    error,
    disabled = false,
    onInputChange,
    style,
    bgColor = 'white',
    required = false,
    requiredColor = 'danger300',
    outline,
    outlineColor = 'white',
    dataTestId,
    textColor = 'neutral900',
    fontSize = 'small',
    radius = 'xxsmall',
    labelOverlapping = false,
    labelBgColor,
    outlineFocusColor,
    ...props
  }: TextAreaProps,
  ref
) => {
  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.currentTarget.value

    !!onInputChange && onInputChange(newValue)
    !!props.onChange && props.onChange(e)
  }

  return (
    <S.Wrapper style={style} disabled={disabled} error={!!error}>
      <S.LabelWrapper>
        {!!label && (
          <S.Label
            htmlFor={name}
            labelColor={labelColor}
            labelSize={labelSize}
            labelWeight={labelWeight}
            labelOverlapping={labelOverlapping}
            labelBgColor={labelBgColor}
          >
            {label}
            {required ? (
              <S.Asterisk requiredColor={requiredColor}>*</S.Asterisk>
            ) : (
              ''
            )}
          </S.Label>
        )}
      </S.LabelWrapper>
      <S.InputWrapper>
        <S.Input
          onChange={onChange}
          disabled={disabled}
          name={name}
          value={props.value}
          bgColor={bgColor}
          ref={ref}
          {...(label ? { id: name } : {})}
          rows={props.rows}
          cols={props.cols}
          placeholder={props.placeholder}
          aria-label={props['aria-label']}
          outline={outline}
          outlineColor={outlineColor}
          outlineFocusColor={outlineFocusColor}
          onBlur={props.onBlur}
          defaultValue={props.defaultValue}
          data-testid={dataTestId}
          textColor={textColor}
          fontSize={fontSize}
          radius={radius}
        />
      </S.InputWrapper>
      {!!error && <S.Error>{error}</S.Error>}
    </S.Wrapper>
  )
}

export default React.forwardRef(TextAreaNew)
